import { useFormik } from "formik";
import React, { useContext, useEffect, useRef, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import { Link } from "react-router-dom";
import moment from "moment";
import { NoteState } from "../../context/notes/NoteState";
import UserServices from "../../UserServices/UserServices";
import { postAssesment } from "../schemas";
import AssessmentFormPrint, {
  downloadAssessmentFormPdf,
  LIFESTYLE_ROWS,
  MEDICAL_ROWS,
  POSTURE_ROWS,
} from "./AssessmentFormPrint";

const TRAINING_GOALS = [
  "Fat Loss",
  "Muscle Gain",
  "Strength",
  "Rehab",
  "General Fitness",
  "Other",
];

const YES_NO = ["Yes", "No"];

const LIFESTYLE_OPTIONS = {
  activity_level: ["Sedentary", "Moderate", "Active"],
  diet_type: ["Balanced", "High Carb", "High Fat", "Poor"],
  smoking_substance: YES_NO,
  stress_level: ["Low", "Moderate", "High"],
};

/** Label + input, wired to formik. */
const Field = ({
  label,
  name,
  form,
  type = "text",
  unit,
  required,
  placeholder,
  options,
  rows,
  readOnly,
  col = "col-12 col-md-4",
}) => {
  const { values, errors, touched, handleChange, handleBlur } = form;
  const error = errors[name] && touched[name] ? errors[name] : null;

  return (
    <div className={col}>
      <div className="form-group custom-group">
        <label className="form-label">
          {label}
          {required ? <span className="text-danger">*</span> : null}
        </label>
        {options ? (
          <select
            className="form-select"
            name={name}
            value={values[name]}
            onChange={handleChange}
            onBlur={handleBlur}
          >
            <option value="">Select…</option>
            {options.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        ) : rows ? (
          <textarea
            className="form-control"
            rows={rows}
            name={name}
            placeholder={placeholder || label}
            value={values[name]}
            onChange={handleChange}
            onBlur={handleBlur}
          />
        ) : unit ? (
          <div className="input-group">
            <input
              type={type}
              className="form-control"
              autoComplete="off"
              placeholder={placeholder || label}
              name={name}
              value={values[name]}
              onChange={handleChange}
              onBlur={handleBlur}
              readOnly={readOnly}
            />
            <span className="input-group-text">{unit}</span>
          </div>
        ) : (
          <input
            type={type}
            className="form-control"
            autoComplete="off"
            placeholder={placeholder || label}
            name={name}
            value={values[name]}
            onChange={handleChange}
            onBlur={handleBlur}
            readOnly={readOnly}
          />
        )}
        {error ? <p className="text-danger">{error}</p> : null}
      </div>
    </div>
  );
};

const SectionTitle = ({ children, note }) => (
  <div className="col-12 mt-2">
    <h6 className="fw-semibold font-16 mb-0">
      {children}
      {note ? (
        <small className="text-muted fw-normal ms-2">{note}</small>
      ) : null}
    </h6>
    <hr className="mt-2 mb-0" />
  </div>
);

export default function PostAssessmentForm(props) {
  const { state } = useContext(NoteState);
  const { branch_id } = state;
  const { id, length } = props;

  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [submission, setSubmission] = useState(false);
  const [myBranches, setMyBranches] = useState([]);
  const [client, setClient] = useState(null);
  const printRef = useRef(null);

  const initialValues = {
    branch_id: branch_id,
    date: moment().format("YYYY-MM-DD"),
    trainer_name: "",

    // Section 1
    Age: "",
    gender: "",
    contact_no: "",
    emergency_contact_no: "",
    training_goal: "",
    training_goal_other: "",

    // Section 2 — medical screening
    ...MEDICAL_ROWS.reduce((acc, row) => ({ ...acc, [row.key]: "" }), {}),
    med_other_condition: "",

    // Section 3 — body stats
    Height: "",
    Weight: "",
    Fat: "",
    Vfat: "",
    BMI: "",

    // Section 4 — measurements
    Chest: "",
    upper_belly: "",
    Waist: "",
    lower_belly: "",
    Glutes: "",
    Thigh: "",
    Arm: "",

    // Section 5 — lifestyle
    occupation: "",
    activity_level: "",
    sleep_hours: "",
    water_intake: "",
    diet_type: "",
    smoking_substance: "",
    stress_level: "",

    // Section 6 — cardiovascular
    vo2_max: "",
    hr_recovery_step_test: "",
    Mhr: "",
    Rhr: "",

    // Section 7 — strength
    max_push_ups: "",
    max_push_ups_one_min: "",
    one_rm_squat: "",
    one_rm_bench_press: "",

    // Section 8 — mobility
    fit_and_reach: "",
    Whr: "",

    // Section 6 — postural
    ...POSTURE_ROWS.reduce((acc, row) => ({ ...acc, [row.key]: "" }), {}),
    clinical_recommendation: "",

    // Sections 7 and 8
    primary_goal: "",
    secondary_goal: "",
    trainer_notes: "",
  };

  const addClientAssessment = async (values, action) => {
    try {
      setLoading(true);
      setSubmission(true);
      await UserServices.addPostAssessment(values, id);
      toast.success("Added Successfully!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: true,
        theme: "colored",
      });
      action.resetForm({ values: { ...initialValues, ...clientDefaults() } });
    } catch (err) {
      console.log(err);
      toast.error(
        err?.response?.data?.message?.[Object.keys(err?.response?.data?.message || {})[0]]?.[0] ||
          "Something went wrong!",
        {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: true,
          theme: "colored",
        }
      );
    } finally {
      setLoading(false);
    }
  };

  const form = useFormik({
    initialValues,
    validationSchema: postAssesment,
    enableReinitialize: false,
    onSubmit: (values, action) => addClientAssessment(values, action),
  });

  const clientDefaults = () => ({
    gender: client?.gender && client.gender !== "N/A" ? client.gender : "",
    contact_no: client?.phone || "",
    emergency_contact_no: client?.emergency_contact_no || "",
  });

  const loadBranches = async () => {
    try {
      const res = await UserServices.branchName();
      const rows = (res?.data?.data || []).map((v) => ({
        id: v.id,
        name: v.name,
      }));
      setMyBranches([{ name: "Select Branches", id: "" }, ...rows]);
    } catch (err) {
      console.log(err);
    }
  };

  const loadClient = async () => {
    if (!id) return;
    try {
      const res = await UserServices.getClientsNameById(branch_id, id);
      const row = res?.data?.data?.[0];
      if (!row) return;
      setClient(row);
      form.setValues((prev) => ({
        ...prev,
        gender:
          prev.gender || (row.gender && row.gender !== "N/A" ? row.gender : ""),
        contact_no: prev.contact_no || row.phone || "",
        emergency_contact_no:
          prev.emergency_contact_no || row.emergency_contact_no || "",
      }));
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    loadBranches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadClient();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, branch_id]);

  const clientName = client
    ? `${client.first_name || ""} ${client.last_name || ""}`.trim()
    : "";

  /** Formik values mapped onto the printed form / DB field names. */
  const printData = {
    ...form.values,
    client_name: clientName,
    age: form.values.Age,
    height: form.values.Height,
    weight: form.values.Weight,
    fat: form.values.Fat,
    v_fat: form.values.Vfat,
    body_mass_index: form.values.BMI,
    chest: form.values.Chest,
    mid_belly: form.values.Waist,
    hips: form.values.Glutes,
    thigh: form.values.Thigh,
    arm: form.values.Arm,
    rhr: form.values.Rhr,
    mhr: form.values.Mhr,
    whr: form.values.Whr,
    one_rm_squats: form.values.one_rm_squat,
  };

  const handleDownload = async () => {
    setPdfLoading(true);
    try {
      await new Promise((r) => requestAnimationFrame(() => setTimeout(r, 150)));
      await downloadAssessmentFormPdf(
        printRef.current,
        clientName || `client_${id}`
      );
    } catch (err) {
      console.log(err);
      toast.error("Could not generate the PDF.");
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <>
      <ToastContainer />
      <form onSubmit={form.handleSubmit}>
        <div className="card-body">
          <h5>
            Client Assessment Form
            <div className="float-end">
              <button
                type="button"
                className="btn btn-outline-dark btn-sm ms-2"
                onClick={handleDownload}
                disabled={pdfLoading}
              >
                <i className="bi bi-download me-1" />
                {pdfLoading ? "Preparing…" : "Download Form"}
              </button>
              {length > 0 || submission ? (
                <Link
                  to={`/viewAssessmentInfo/${id}`}
                  className="ms-2 btn btn-primary btn-sm"
                >
                  View Assessments
                </Link>
              ) : null}
            </div>
            <div className="clearfix" />
          </h5>

          <p className="mb-2">
            <i className="bi bi-exclamation-lg text-danger" aria-hidden="true" />
            The Fields With <strong className="text-danger">*</strong>
            Must Required Or Fill.
          </p>

          <div className="row g-3">
            <SectionTitle>Assessment Details</SectionTitle>
            <div className="col-12 col-md-4">
              <div className="form-group custom-group">
                <label className="form-label">
                  Branch Name<span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  name="branch_id"
                  disabled={branch_id ? true : false}
                  value={form.values.branch_id}
                  onChange={form.handleChange}
                  onBlur={form.handleBlur}
                >
                  {myBranches.map((el, index) => (
                    <option key={index} value={el.id}>
                      {el.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <Field
              label="Assessment Date "
              name="date"
              type="date"
              form={form}
              required
            />
            <Field
              label="Trainer Name"
              name="trainer_name"
              form={form}
              placeholder="Defaults to the logged-in trainer"
            />

            <SectionTitle>Section 1 — Client Information</SectionTitle>
            <div className="col-12 col-md-4">
              <div className="form-group custom-group">
                <label className="form-label">Client</label>
                <input
                  className="form-control"
                  value={clientName || `#${id}`}
                  readOnly
                />
              </div>
            </div>
            <Field label="Age " name="Age" type="number" form={form} required />
            <Field
              label="Gender"
              name="gender"
              form={form}
              options={["Male", "Female", "Other"]}
            />
            <Field label="Contact" name="contact_no" form={form} />
            <Field
              label="Emergency Contact No"
              name="emergency_contact_no"
              form={form}
            />
            <Field
              label="Training Goal"
              name="training_goal"
              form={form}
              options={TRAINING_GOALS}
            />
            {form.values.training_goal === "Other" ? (
              <Field
                label="Other Goal"
                name="training_goal_other"
                form={form}
                col="col-12 col-md-8"
              />
            ) : null}

            <SectionTitle note='Mark "Yes" if applicable'>
              Section 2 — Medical &amp; Health Screening
            </SectionTitle>
            {MEDICAL_ROWS.map((row) => (
              <Field
                key={row.key}
                label={row.label}
                name={row.key}
                form={form}
                options={YES_NO}
                col="col-12 col-md-4 col-xl-3"
              />
            ))}
            <Field
              label="Any Other Condition"
              name="med_other_condition"
              form={form}
              col="col-12 col-md-8"
            />

            <SectionTitle>Section 3 — Body Stats</SectionTitle>
            <Field
              label="Height "
              name="Height"
              type="number"
              unit="cm"
              form={form}
              required
            />
            <Field
              label="Weight "
              name="Weight"
              type="number"
              unit="Kg"
              form={form}
              required
            />
            <Field
              label="Body FAT "
              name="Fat"
              type="number"
              unit="%"
              form={form}
              required
            />
            <Field
              label="V-FAT "
              name="Vfat"
              type="number"
              unit="%"
              form={form}
              required
            />
            <Field
              label="BMI "
              name="BMI"
              type="number"
              unit="kg/m2"
              form={form}
              required
            />

            <SectionTitle>Section 4 — Measurements (cm)</SectionTitle>
            <Field
              label="Chest "
              name="Chest"
              type="number"
              unit="cm"
              form={form}
              required
            />
            <Field
              label="Upper Belly (5 cm above navel)"
              name="upper_belly"
              type="number"
              unit="cm"
              form={form}
            />
            <Field
              label="Mid Belly / Waist (at navel) "
              name="Waist"
              type="number"
              unit="cm"
              form={form}
              required
            />
            <Field
              label="Lower Belly (5 cm below navel)"
              name="lower_belly"
              type="number"
              unit="cm"
              form={form}
            />
            <Field
              label="Hips / Glutes "
              name="Glutes"
              type="number"
              unit="cm"
              form={form}
              required
            />
            <Field
              label="Thighs "
              name="Thigh"
              type="number"
              unit="cm"
              form={form}
              required
            />
            <Field
              label="Arms "
              name="Arm"
              type="number"
              unit="cm"
              form={form}
              required
            />

            <SectionTitle>Section 5 — Lifestyle &amp; Habits</SectionTitle>
            {LIFESTYLE_ROWS.map((row) => (
              <Field
                key={row.key}
                label={row.label}
                name={row.key}
                form={form}
                options={LIFESTYLE_OPTIONS[row.key]}
                placeholder={row.hint}
              />
            ))}

            <SectionTitle note="Optional">
              Section 6 — Postural Assessment
            </SectionTitle>
            {POSTURE_ROWS.map((row) => (
              <Field
                key={row.key}
                label={row.label}
                name={row.key}
                form={form}
                placeholder="Observations"
              />
            ))}
            <Field
              label="Clinical Recommendation"
              name="clinical_recommendation"
              form={form}
              rows={2}
              col="col-12"
            />

            <SectionTitle>Section 7 — Training Objectives</SectionTitle>
            <Field
              label="Primary Goal"
              name="primary_goal"
              form={form}
              col="col-12 col-md-6"
            />
            <Field
              label="Secondary Goal"
              name="secondary_goal"
              form={form}
              col="col-12 col-md-6"
            />

            <SectionTitle>Section 8 — Trainer Final Notes</SectionTitle>
            <Field
              label="Trainer Final Notes"
              name="trainer_notes"
              form={form}
              rows={3}
              col="col-12"
            />
          </div>
        </div>
        <div className="clearfix" />

        <div className="col-12 text-center mb-3">
          <button
            type="submit"
            className="btn btn-primary px-5 mb-1 me-2 addclient-button"
          >
            Add Results
          </button>
          <button
            type="button"
            className="btn btn-outline-dark px-4 mb-1"
            onClick={handleDownload}
            disabled={pdfLoading}
          >
            <i className="bi bi-file-earmark-pdf me-1" />
            {pdfLoading ? "Preparing…" : "Download Form (PDF)"}
          </button>
        </div>
      </form>

      {/* Hidden A4 layout used to build the PDF */}
      <div className="caf-print-host" aria-hidden="true">
        <div ref={printRef}>
          <AssessmentFormPrint data={printData} />
        </div>
      </div>

      <div className={`loader ${loading ? "in" : ""}`}>
        <div className="spinner-border main-spin"></div>
      </div>
    </>
  );
}
